import { Box, Typography, Colors, images } from "@analyticsMD/quil";
import { css } from "@emotion/css";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

import type { FC, ReactNode } from "react";
import type { MalnutritionPatientSummary } from "../../types/Malnutrition";

const styles = {
  wrapper: css`
    background: url("${images.PatientBackground}") ${Colors.qlavender_80};
    background-size: cover;
    color: ${Colors.white};
    padding: 1rem 1rem 1.25rem 1rem;
    box-sizing: border-box;
  `,
  topRow: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.5rem;
  `,
  iconButton: css`
    color: ${Colors.white};
    background-color: rgba(255, 255, 255, 0.15);
    width: 1.75rem;
    height: 1.75rem;
  `,
};

const sxStyles = {
  name: {
    fontSize: "1.625rem",
    fontWeight: "bold",
    lineHeight: 1.2,
    wordBreak: "break-word",
    flex: 1,
    minWidth: 0,
  },
  demographics: {
    fontSize: "0.75rem",
    marginTop: "0.5rem",
    opacity: 0.95,
  },
  iconButtonRoot: {
    color: Colors.white,
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    width: "1.75rem",
    height: "1.75rem",
    padding: "0.25rem",
    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.25)" },
  },
};

interface MalnutritionHeaderProps {
  patient: MalnutritionPatientSummary;
  onExpand?: () => void;
  onClose?: () => void;
  /** Override the close affordance when the host provides one (e.g., drawer chrome). */
  closeButtonCmpt?: ReactNode;
}

const MalnutritionHeader: FC<MalnutritionHeaderProps> = ({
  patient,
  onExpand,
  onClose,
  closeButtonCmpt,
}) => {
  const demographics = `Bed ${patient.current_bed} · ${patient.age}${patient.sex} · ${patient.current_unit} · MRN ${patient.mrn}`;

  return (
    <Box className={styles.wrapper}>
      <Box className={styles.topRow}>
        <Typography sx={sxStyles.name}>{patient.full_name}</Typography>
        <Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {onExpand && (
            <IconButton
              size="small"
              onClick={onExpand}
              aria-label="Expand"
              sx={sxStyles.iconButtonRoot}
            >
              <OpenInFullIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          )}
          {closeButtonCmpt ??
            (onClose && (
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="Close"
                sx={sxStyles.iconButtonRoot}
              >
                <CloseIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            ))}
        </Box>
      </Box>
      <Typography sx={sxStyles.demographics}>{demographics}</Typography>
    </Box>
  );
};

export default MalnutritionHeader;
